import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem as MuiMenuItem,
  MenuList,
  Paper,
  Popper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { RolePermissions } from "../../auth/rolePermissions";
import { MenuGroup, MenuItem, SHOW_PLACEHOLDERS, menuConfig } from "./menuConfig";
import { MenuAccordionSection } from "./menu/MenuAccordionSection";
import { DashboardIcon, menuIconMap } from "./menu/menuIcons";
import { DASHBOARD_ROUTE, useActiveMenuRoute } from "./menu/activeRoute";
import { menuRowSx, railButtonSx } from "./menu/menuStyles";
import { NAVBAR_HEIGHT } from "../common/layoutConstants";

export const SIDEBAR_WIDTH = 260;
export const RAIL_WIDTH = 64;


/** Menu groups with only the items this role may see (placeholders per SHOW_PLACEHOLDERS); groups with none are dropped. */
export const useVisibleMenu = (): { group: MenuGroup; items: MenuItem[] }[] => {
  const { user } = useAuth();
  return useMemo(() => {
    const permissions = user ? RolePermissions[user.role] ?? [] : [];
    return menuConfig
      .map((group) => ({
        group,
        items: group.items.filter(
          (item) => permissions.includes(item.permission) && (SHOW_PLACEHOLDERS || item.status === "active")
        ),
      }))
      .filter((entry) => entry.items.length > 0);
  }, [user]);
};

const scrollbarSx = {
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0, 0, 0, 0.25)", borderRadius: "4px" },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
};

/** A group shown as one direct link (MenuGroup.link), styled like the Dashboard row. */
const MenuLinkRow = ({ group, item, active, onNavigate }: { group: MenuGroup; item: MenuItem; active: boolean; onNavigate: () => void }) => {
  const GroupIcon = menuIconMap[group.icon];
  return (
    <ListItem disablePadding>
      <ListItemButton component={Link as React.ElementType} to={item.route} onClick={onNavigate} selected={active} sx={menuRowSx}>
        <ListItemIcon>
          <GroupIcon />
        </ListItemIcon>
        <ListItemText primary={group.label} />
      </ListItemButton>
    </ListItem>
  );
};

/** Full menu: Dashboard + accordion groups. The group of the current page opens on its own. */
const FullMenu = ({ onNavigate }: { onNavigate: () => void }) => {
  const visible = useVisibleMenu();
  const activeRoute = useActiveMenuRoute();
  const activeGroup = visible.find((entry) => entry.items.some((item) => item.route === activeRoute))?.group.label;

  // Only one section open at a time — expanding one collapses any other.
  const [expandedGroup, setExpandedGroup] = useState<string | false>(activeGroup ?? false);
  useEffect(() => {
    if (activeGroup) setExpandedGroup(activeGroup);
  }, [activeGroup]);

  return (
    <List sx={{ py: 1.5 }}>
      <ListItem disablePadding>
        <ListItemButton component={Link as React.ElementType} to="/app/dashboard" onClick={onNavigate} selected={activeRoute === DASHBOARD_ROUTE} sx={menuRowSx}>
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
      </ListItem>
      {visible.map(({ group, items }) =>
        group.link && items.length === 1 ? (
          <MenuLinkRow key={group.label} group={group} item={items[0]} active={activeRoute === items[0].route} onNavigate={onNavigate} />
        ) : (
          <MenuAccordionSection
            key={group.label}
            group={group}
            visibleItems={items}
            onNavigate={onNavigate}
            expanded={expandedGroup === group.label}
            onChange={(_e, isExpanded) => setExpandedGroup(isExpanded ? group.label : false)}
          />
        )
      )}
    </List>
  );
};

/** Icon rail: one icon per group; a one-item group is a direct link, others open a flyout. */
const RailMenu = () => {
  const activeRoute = useActiveMenuRoute();
  const visible = useVisibleMenu();
  const [flyout, setFlyout] = useState<{ anchor: HTMLElement; group: MenuGroup; items: MenuItem[] } | null>(null);
  const railRef = useRef<HTMLUListElement | null>(null);

  // Clicking a group icon opens its flyout, switching straight from another one; clicking the open one closes it
  const toggleFlyout = (anchor: HTMLElement, group: MenuGroup, items: MenuItem[]) =>
    setFlyout((current) => (current?.group.label === group.label ? null : { anchor, group, items }));

  // Clicks on the rail itself are handled by the icons, so only clicks elsewhere close the flyout
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (railRef.current?.contains(event.target as Node)) return;
    setFlyout(null);
  };

  return (
    <List ref={railRef} sx={{ py: 1.5 }}>
      <Tooltip title="Dashboard" placement="right">
        <ListItemButton component={Link as React.ElementType} to="/app/dashboard" onClick={() => setFlyout(null)} sx={railButtonSx(activeRoute === DASHBOARD_ROUTE)}>
          <DashboardIcon />
        </ListItemButton>
      </Tooltip>

      {visible.map(({ group, items }) => {
        const GroupIcon = menuIconMap[group.icon];
        const active = items.some((item) => item.route === activeRoute);
        const single = items.length === 1 && items[0].status === "active";
        return (
          <Tooltip key={group.label} title={group.label} placement="right">
            {single ? (
              <ListItemButton component={Link as React.ElementType} to={items[0].route} onClick={() => setFlyout(null)} sx={railButtonSx(active)}>
                <GroupIcon />
              </ListItemButton>
            ) : (
              <ListItemButton onClick={(e) => toggleFlyout(e.currentTarget, group, items)} sx={railButtonSx(active)}>
                <GroupIcon />
              </ListItemButton>
            )}
          </Tooltip>
        );
      })}

      {/* flyout with the group's items, to the right of the rail. A non-modal Popper (no backdrop),
          so a click on another rail icon switches flyouts in one click. */}
      <Popper
        open={Boolean(flyout)}
        anchorEl={flyout?.anchor}
        placement="right-start"
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={0}
            sx={{ minWidth: 220, borderRadius: 3, border: "1px solid #e5e7eb", boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)", p: 0.5 }}
          >
            <Typography sx={{ px: 1.5, pt: 1, pb: 0.5, display: "block", fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", color: "#94a3b8", textTransform: "uppercase" }}>
              {flyout?.group.label}
            </Typography>
            <MenuList
              autoFocusItem
              sx={{ p: 0 }}
              onKeyDown={(e) => {
                if (e.key === "Escape") setFlyout(null);
              }}
            >
              {flyout?.items.map((item) => {
                const ItemIcon = menuIconMap[item.icon];
                return (
                  <MuiMenuItem
                    key={item.label}
                    component={Link}
                    to={item.route}
                    disabled={item.status === "placeholder"}
                    selected={item.route === activeRoute}
                    onClick={() => setFlyout(null)}
                    sx={{ borderRadius: 2, fontSize: 14, minHeight: 38, "&.Mui-selected": { bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main", fontWeight: 600 } }}
                  >
                    <ListItemIcon>
                      <ItemIcon fontSize="small" />
                    </ListItemIcon>
                    {item.label}
                    {item.status === "placeholder" && (
                      <Typography variant="caption" color="text.secondary" ml={1}>
                        soon
                      </Typography>
                    )}
                  </MuiMenuItem>
                );
              })}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </List>
  );
};

type Props =
  | { mode: "drawer"; open: boolean; onClose: () => void }
  | { mode: "permanent"; expanded: boolean };

/**
 * Sidebar. Desktop ("permanent"): always visible - full menu (expanded) or an icon rail (collapsed),
 * toggled from the top bar. Phone ("drawer"): slides over the page, closes after a pick.
 */
export const AppSidebar = (props: Props) => {
  if (props.mode === "drawer") {
    return (
      <Drawer
        variant="temporary"
        open={props.open}
        onClose={props.onClose}
        ModalProps={{ keepMounted: true }} // better mobile performance
        sx={{ "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, ...scrollbarSx } }}
      >
        <Toolbar sx={{ height: NAVBAR_HEIGHT }} />
        <FullMenu onNavigate={props.onClose} />
      </Drawer>
    );
  }

  const width = props.expanded ? SIDEBAR_WIDTH : RAIL_WIDTH;
  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        transition: "width 200ms ease",
        "& .MuiDrawer-paper": {
          width,
          overflowX: "hidden",
          transition: "width 200ms ease",
          boxSizing: "border-box",
          borderRight: "1px solid #e5e7eb",
          bgcolor: "#ffffff",
          ...scrollbarSx,
        },
      }}
    >
      <Toolbar sx={{ height: NAVBAR_HEIGHT }} />
      <Box>{props.expanded ? <FullMenu onNavigate={() => undefined} /> : <RailMenu />}</Box>
    </Drawer>
  );
};
